/* eslint-disable class-methods-use-this */
import { observable, action, computed, makeObservable, toJS } from 'mobx';
import { debounce, find, isEmpty, sortBy } from 'lodash-es';
import { getIntl } from 'umi';
import type { InfoStruct, Question, MemberStruct } from '@/types/meeting';
import type { WhiteboardAuth } from '@/types/whiteboard';
import { WhiteboardEnablePermission } from '@/types/whiteboard';
import { clearSessionStorage } from '@/utils/common';
import { Modal, message } from '@/components';
import * as APIs from '@/api';
import { chat, user, rtc, members, focused, transcribe, note } from './index';
import { setChannelId } from '@/api';
import { HandUpStatus } from '@/pages/Meeting/component/EmoJ';
import { RoleType } from '@/pages/Meeting/component/PeoplePanel/pc';
import { NoteAuth } from './note';

function diffByTime(maxtime: number) {
  let temp = '';
  let time = 1;
  return (str: string, force = false) => {
    if (force) {
      temp = str;
      time = maxtime;
      return true;
    }
    if (str === temp) {
      time += 1;
      if (time >= maxtime) {
        return true;
      }
      return false;
    }
    temp = str;
    time = 1;
    return false;
  };
}

const diffStr = diffByTime(2);

export enum MODE {
  GRID = 'grid',
  LECTURER = 'lecturer',
  ONLY = 'only',
  ONLYSHARE = 'onlyShare',
  FOCUSED = 'focused',
}

export enum ShareScreenFull {
  INIT = 'init',
  FULL = 'full',
  EXIT_FULL = 'exitFull',
}
export enum MEETING_STATUS {
  // 未知状态
  UNKNOW = 0,
  // 初始状态
  INIT = 1,
  // 进行中
  STARTED = 2,
  // 正常结束
  END = 3,
  // 异常结束
  ABNORMAL_END = 4,
  // 已关闭
  CLOSED = 5,
}

class Meeting {
  constructor() {
    makeObservable(this);
  }

  @observable info: InfoStruct | null = null;

  @observable questions: Question[] = [];

  @observable shareQuestions: Question[] = [];

  @observable hostUid = '';

  @observable title = '';

  @observable membersMap: Map<string, MemberStruct> = new Map();

  // @observable yxuids: string[] = [];

  @observable screenUid = '';

  @observable whiteboardUid = '';

  @observable wbEnablePermission: WhiteboardEnablePermission = WhiteboardEnablePermission.ALL;

  @observable whiteboardAuth: WhiteboardAuth | undefined = undefined;

  @observable whiteboardChannelId = '';

  @observable whiteboardWillClose = false;

  @observable whiteboardLoading = false;

  @observable codeEditorEnabled = false;

  @observable codeEditorLoading = false;

  @observable codeEditorEnabledUid = '';

  @observable speakerYxUid = '';

  @observable speakerYxUidList: string[] = [];

  @observable lastJoinMember: MemberStruct | null = null;

  @observable createdTime: number = new Date().getTime();

  @observable avatarMap: Map<number, any> = new Map();

  @observable avatarIds: number[] = [];

  @observable defaultAvatarId = 0;

  @observable uidToYxuidMap: Map<string, string> = new Map();

  @observable deviceIdToYxuidMap: Map<string, number> = new Map();

  @observable midToYxuidMap: Map<string, number> = new Map();

  @observable chatIdToYxuidMap: Map<string, string> = new Map();

  @observable gridBoxWidth = 0;

  @observable gridBoxHeight = 0;

  @observable lecturerBoxWidth = 0;

  @observable lecturerBoxHeight = 0;

  @observable peoplePanelShow = !window.isMobile;
  @observable userManualSetPeoplePanel = false;

  @observable viewMode = MODE.ONLY;

  @observable userSelectedViewMode: MODE | '' = '';

  @observable meetingStatus: MEETING_STATUS = MEETING_STATUS.UNKNOW;

  @observable reactionData: any = {};

  @observable temporaryUnmute = false;

  @computed
  get rightPanelShow() {
    return this.peoplePanelShow || chat.chatVisible || transcribe?.transcribeVisible;
  }
  @computed // 代码编辑器应用权限
  get codeEditorShouldBeOpen() {
    return !!this.ableAppsMap.G37CodeEditor;
  }

  // G37CodeEditor: 代码编辑器;
  @observable ableAppsMap: Record<string, boolean> = {
    G37CodeEditor: false,
    WebRecording: false,
    Interpretation: false,
    EmoticonReaction: false,
    Subtitle: false,
    Transcription: false,
    WebTranscriptionLanguage: false,
  };

  // 会议录制状态：1-录制中，2-已暂停，3-已停止；默认状态 3-已停止。
  @observable videoRecordingState = 3;
  @observable shareScreenFull = ShareScreenFull.INIT;

  @action setShareScreenFull(full: ShareScreenFull) {
    this.shareScreenFull = full;
  }
  @action updateRectionMap(yuxid: string, data: any) {
    this.reactionData[yuxid] = data;
  }
  @action
  setViewMode(mode: MODE, isUser?: boolean) {
    this.viewMode = mode;
    if (typeof isUser === 'boolean' && isUser) {
      this.userSelectedViewMode = mode;
    }
  }

  @action
  setVideoRecording(sates: number) {
    this.videoRecordingState = sates;
  }

  @action
  getVideoRecording() {
    this.info?.channelId &&
      APIs.getRecordingStatus(this.info?.channelId).then((res) => {
        this.videoRecordingState = res.data?.status || 3;
      });
  }

  @action
  async getFeedbackQuestion(
    meetingId: string,
    cid: number,
    uid: string,
    locale: string,
    docType = 2
  ) {
    // 1 中文 2 英文 3 日文
    let lang = 'zh';
    if (locale.includes('en')) {
      lang = 'en';
    }
    if (locale.includes('ja')) {
      lang = 'ja';
    }
    try {
      const resp = await APIs.getFeedbackQuestion(meetingId, lang, docType);
      if (resp.data) {
        this.questions = resp.data;
      }
    } catch (err) {
      console.log(err, 'get feedback question error');
    }
  }

  @action
  setPeoplePanelShow(show: boolean, manual?: boolean): any {
    this.peoplePanelShow = show;
    this.userManualSetPeoplePanel = manual || false;
    // if (this.shareScreenFull === ShareScreenFull.FULL) {
    //   this.setShareScreenFull(ShareScreenFull.EXIT_FULL);
    // }
  }

  @action
  getAppsState() {
    APIs.getAvailableApps(this.info?.meetingId, this.info?.channelId).then((res) => {
      if (res.status === 1) {
        const data: Record<string, boolean> = {};
        res.data?.forEach((n) => {
          n.appId && (data[n.appId] = true);
        });
        this.ableAppsMap = data;
      }
    });
  }

  @action
  async getInfo(
    params: {
      meetingId: string;
      scheduleSt?: string;
      firstMemberNotifyOn?: string;
      password?: string;
    },
    isLogin: boolean
  ) {
    let resp;
    const { meetingId } = params;
    if (!isLogin) {
      resp = await APIs.getMeetingInfo(meetingId);
      this.meetingStatus = resp.data?.status as MEETING_STATUS;
      // this.meetingStatus = MEETING_STATUS.STARTED;
    } else {
      resp = await APIs.meetingCheckIn(params);
    }
    const data = resp.data as InfoStruct;
    setChannelId(data?.channelId?.toString() || '');
    this.info = data;
    this.title = data.meetingName;
    if (data.screenShareStatus?.shareMemberUid && data.screenShareStatus?.shareMemberDeviceId) {
      this.screenUid =
        data.screenShareStatus.shareMemberUid + data.screenShareStatus.shareMemberDeviceId;
      this.peoplePanelShow = false; // 共享的时候关闭成员列表
    }
    data.createtime && (this.createdTime = data.createtime);
    this.updateMemberList(data.members!, false);
    data.whiteboardStatus?.wbEnablePermission &&
      (this.wbEnablePermission = data.whiteboardStatus.wbEnablePermission);
    if (!resp?.data?.cid) {
      clearSessionStorage();
    } else {
      this.getVideoRecording();
      transcribe.getTranscribeStatus();
      this.getAppsState();
      // 去获取议员列表信息  为什么要放这里? 因为这个地方能拿到channelId TODO: 需要针对此处做改造
      members.getInterpreters({ channelId: data.channelId! });
    }
    setTimeout(() => {
      if (!data.chatRoomEnabled) {
        chat.setIsAbleUse(false);
      } else {
        chat.setIsAbleUse(true);
      }
    });

    // 初始化设置当前专注人
    const focusOnMemberUid = data.focusModeStatus?.focusOnMemberUid;
    const focusedYuxid = data.members!.find((member) => member.uid === focusOnMemberUid)?.yxUidStr;
    if (focusedYuxid) {
      focused?.setFocusedYuxid(focusedYuxid);
    }

    // 初始化设置共享专注状态
    if (data?.shareUid && data.screenShareStatus?.shareAnnotateEnabled!) {
      note?.setShareAnnotateEnabled(data.screenShareStatus?.shareAnnotateEnabled!);
      note?.setShareAnnotatePermission(data.screenShareStatus?.shareAnnotatePermission!);
      note?.setShareAnnotateAuth(data.screenShareStatus?.shareAnnotateWbYxAuthArgs! as NoteAuth);
      note?.setOriginSize(
        data.screenShareStatus?.shareSourceWidth || 0,
        data.screenShareStatus?.shareSourceHeight || 0
      );
    }
    note?.setShareMemberDeviceType(data?.screenShareStatus?.shareMemberDeviceType!);

    return resp;
  }

  @action
  async getAvatarList() {
    const res = await APIs.getAvatarList();
    this.avatarIds = [];
    res.data?.forEach((avatar: any) => {
      this.avatarIds.push(avatar.avatarId);
      if (!this.avatarMap.has(avatar.avatarId)) {
        this.avatarMap.set(avatar.avatarId, {
          micOn: {
            default: avatar.micOnAvatarUrl,
            '36': avatar.micOnAvatarUrl36,
            '68': avatar.micOnAvatarUrl68,
            '100': avatar.micOnAvatarUrl100,
            '120': avatar.micOnAvatarUrl120,
          },
          micOff: {
            default: avatar.micOffAvatarUrl,
            '36': avatar.micOffAvatarUrl36,
            '68': avatar.micOffAvatarUrl68,
            '100': avatar.micOffAvatarUrl100,
            '120': avatar.micOffAvatarUrl120,
          },
          speaking: {
            default: avatar.micOnSayingAvatarUrl,
            '36': avatar.micOnSayingAvatarUrl,
            '68': avatar.micOnSayingAvatarUrl,
            '100': avatar.micOnSayingAvatarUrl,
            '120': avatar.micOnSayingAvatarUrl120,
          },
          cover: {
            default: avatar.coverAvatarUrl,
            '36': avatar.coverAvatarUrl,
            '68': avatar.coverAvatarUrl,
            '100': avatar.coverAvatarUrl,
            '320': avatar.coverAvatarUrl320,
          },
        });
      }
    });
    const { size } = this.avatarMap;
    if (size > 0) {
      this.defaultAvatarId = this.avatarIds[Math.floor(Math.random() * size)];
    }
  }
  @action updateMemberListByVideo() {
    let members = [...this.membersMap.values()].filter((n) => n && n.status === 7);
    if (members.length > 9) {
      // 只对首页<=9人才做此排序
      return;
    }
    members = members.map((item) => {
      let isOpenVideo;
      if (user?.yxuid === item.yxUidStr) {
        isOpenVideo = rtc?.isOpenVideo;
      } else {
        isOpenVideo = !!rtc?.remoteStreamsMap.get(Number(item.yxUid))?.video;
      }
      return {
        isOpenVideo,
        ...item,
      };
    });
    const openCameraArr = members.filter((item) => item.isOpenVideo);
    const notOpenCameraArr = members.filter((item) => !item.isOpenVideo);
    members = openCameraArr.concat(notOpenCameraArr);
    const map = new Map();
    members.map((item) => {
      map.set(String(item?.yxUid), item);
    });
    this.membersMap = map;
  }
  @action
  updateMemberList(list: MemberStruct[], join: boolean) {
    if (!list?.length) return;
    const map = new Map(this.membersMap);
    const newList = list
      .map((item) => {
        if (String(item.yxUid) === user.yxuid) {
          item.inTime = new Date().getTime();
        }
        return item;
      })
      .sort((a, b) => {
        return (a?.inTime || 0) - (b?.inTime || 0);
      });

    newList.forEach((item: MemberStruct, index: number) => {
      if (!join) {
        item.status = 0;
      } else {
        item.status = 7;
      }

      map.set(String(item.yxUid), item);
      this.uidToYxuidMap.set(String(item.uid), String(item.yxUid));
      this.deviceIdToYxuidMap.set(String(item.deviceId), item.yxUid || 0);
      this.midToYxuidMap.set(String(item.mid), item.yxUid || 0);
      this.chatIdToYxuidMap.set(String(item.yxImAccid), String(item.yxUid));
      if (index === list.length - 1) {
        this.lastJoinMember = item;
      }
      if (item.uid === user.uid) {
        user.setAvatarUrl(item.memberAvatarUrl);
      }
      // role -1 未知，0 支持人，1 普通参会人，2游客参会人
      // 目前的逻辑屏幕录制只有主持人才可以用 其他能显示按钮的都禁用态
      if (item.role === 0) {
        this.hostUid = item.mid;
      }
    });
    this.membersMap = map;
  }

  @action
  updateMemberYxuids(yxUids: string[], join: boolean) {
    yxUids.forEach((id) => {
      // @ts-ignore
      const item = this.membersMap.get(id);
      if (item) {
        this.membersMap.set(id, {
          ...item,
          status: join ? 7 : 0,
        });
      }
    });
  }

  @action // 更新列表的举手状态
  updateMemberHandUpStatusYxuids(yxUids: string[], handupStatus: HandUpStatus) {
    yxUids.forEach((id) => {
      // @ts-ignore
      const item = this.membersMap.get(`${id}`);
      if (item) {
        this.membersMap.set(`${id}`, {
          ...item,
          handupStatus,
        });
      }
    });
  }

  @action // 更新列表的联席主持人状态
  updateMemberJoinHostStatus(grantedJointHosts: any, revokedJointHosts: any) {
    if (grantedJointHosts?.length) {
      grantedJointHosts.map((item: { jointHostUid: string }) => {
        const coHost = find([...this.membersMap.values()], { uid: item.jointHostUid });
        if (coHost) {
          this.membersMap.set(`${coHost.yxUid}`, {
            ...coHost,
            role: RoleType.COHOST,
          });
        }
      });
    }
    if (revokedJointHosts?.length) {
      revokedJointHosts.map((item: { jointHostUid: string }) => {
        const coHost = find([...this.membersMap.values()], { uid: item.jointHostUid });
        if (coHost) {
          this.membersMap.set(`${coHost.yxUid}`, {
            ...coHost,
            role: RoleType.GUEST,
          });
        }
      });
    }
  }

  @action
  async queryMemberInfoByYXUid(cid: number, yxUids: number[]) {
    try {
      const ids = yxUids.map((n) => n.toString());
      const hasYxUids = ids.filter((id) => this.membersMap.get(id.toString()));
      const noYxUids = ids.filter((id) => !this.membersMap.get(id.toString()));
      if (hasYxUids) {
        this.updateMemberYxuids(hasYxUids, true);
      }
      if (noYxUids.length) {
        const resp = await APIs.queryMemberInfoByYXUid(cid, noYxUids);
        this.updateMemberList(resp.data || [], true);
      }
    } catch (err: any) {
      console.warn('获取入会人员数据报错', err);
      throw err;
    }
  }

  @action
  setHostUid(host: string) {
    this.hostUid = host;
  }

  @computed
  get hostYxUid() {
    return this.midToYxuidMap.get(this.hostUid);
  }

  @action
  setTitle(title: string) {
    this.title = title;
  }

  @action
  removeMember(yxuid: number) {
    const map = new Map(this.membersMap);
    const user = map.get(String(yxuid));
    this.hostUid === user?.mid && (this.hostUid = '');
    this.uidToYxuidMap.delete(String(user?.uid));
    user?.deviceId && this.deviceIdToYxuidMap.delete(user?.deviceId);
    user?.mid && this.midToYxuidMap.delete(user?.mid);
    map.delete(String(yxuid));
    this.membersMap = map;
  }

  @action
  setScreenUid(uid: string) {
    this.screenUid = uid;
  }

  @action
  setWhiteboardUid(uid: string, auth?: WhiteboardAuth, channelId?: string) {
    this.whiteboardUid = uid;
    this.whiteboardAuth = auth;
    this.whiteboardChannelId = channelId || '';
  }

  @action
  setWhiteboardLoading(loading: boolean) {
    this.whiteboardLoading = loading;
  }

  @action
  setWhiteboardWillClose(value: boolean) {
    this.whiteboardWillClose = value;
  }

  @action
  setwbEnablePermission(value: number) {
    this.wbEnablePermission = value;
  }

  @action
  setCodeEditorEnabled(value: boolean) {
    this.codeEditorEnabled = value;
  }

  @action
  setCodeEditorEnableUid(uid: string) {
    this.codeEditorEnabledUid = uid;
  }

  @action
  setCodeEditorLoading(loading: boolean) {
    this.codeEditorLoading = loading;
  }

  @action.bound
  closeEditor() {
    const { formatMessage } = getIntl();
    Modal.confirm({
      title: formatMessage({ id: 'CLOSE_COLEDITOR' }),
      content: formatMessage({ id: 'WILL_CLOSE_FOR_ALL_MEETING_ATTENDEES' }),
      cancelText: formatMessage({ id: 'CANCEL' }),
      okText: formatMessage({ id: 'CLOSE' }),
      onOk: (close) => {
        APIs.disableCodeEditor(rtc.cid)
          .then(
            action(() => {
              this.setCodeEditorEnabled(false);
              close();
            })
          )
          .catch((e) => {
            console.error('failed to disable code editor.', e);
            message.error({ content: e.message || String(e) });
          });
      },
      okButtonProps: {
        danger: true,
      },
    });
  }

  @action
  setSpeakerYxUid(yxuid: string) {
    if (!yxuid && !this.speakerYxUid) return;
    if (String(yxuid) === String(user.yxuid)) {
      !this.speakerYxUid && (this.speakerYxUid = yxuid);
    } else {
      if (diffStr(yxuid)) {
        this.speakerYxUid = yxuid;
      }

      this.clearSpeakerYxUidDebounce();
    }
  }

  @action
  clearSpeakerYxUid() {
    diffStr('', true);
    if (this.viewMode !== MODE.LECTURER || focused?.focusedYuxid) this.speakerYxUid = '';
  }

  @action
  lectureClearSpeakerYxUid() {
    this.speakerYxUid = '';
  }

  clearSpeakerYxUidDebounce = debounce(this.clearSpeakerYxUid, 3000);

  @action
  setSpeakerYxUidList(uid: string | number | Array<string | number>) {
    let arr = JSON.parse(JSON.stringify(this.speakerYxUidList));
    if (typeof uid === 'string' || typeof uid === 'number') {
      arr.push(uid);
    } else {
      arr = uid;
    }
    const set: any = new Set(arr);
    this.speakerYxUidList = [...set];
  }

  @action
  setGridBoxWidth(width: number) {
    this.gridBoxWidth = width;
  }

  @action
  setGridBoxHeight(height: number) {
    this.gridBoxHeight = height;
  }

  @action
  setLecturerBoxWidth(width: number) {
    this.lecturerBoxWidth = width;
  }

  @action
  setLecturerBoxHeight(height: number) {
    this.lecturerBoxHeight = height;
  }

  @action
  deleteYxuid(uid: number) {
    this.membersMap.delete(uid.toString());
  }

  @computed
  get screenUserInfo() {
    return this.getMemberInfoByMid(this.screenUid);
  }

  // 根据权重大小交换会议成员的位置
  @action updateMemberMap(backIndex: number, previousIndex: number) {
    const members = [...this.membersMap.values()].filter((n) => n && n.status === 7);
    const temp = members[backIndex];
    members[backIndex] = members[previousIndex];
    members[previousIndex] = temp;
    const map = new Map();
    members.map((item) => {
      map.set(String(item?.yxUid), item);
    });
    this.membersMap = map;
  }

  @action setTemporaryUnmute(data: boolean) {
    this.temporaryUnmute = data;
  }

  @computed
  get sortMembers() {
    const list = [...this.membersMap.values()].filter((n) => n && n.status === 7);
    return list.sort((a, b) => {
      if (a.yxUidStr === user.yxuid) return -1;
      if (b.yxUidStr === user.yxuid) return 1;
      return 0;
    });
    // .sort((a, b) => {
    //   // if (a.yxUidStr === this.screenUserInfo?.yxUid?.toString()) {
    //   //   return -1;
    //   // }
    //   // if (b.yxUidStr === this.screenUserInfo?.yxUid?.toString()) {
    //   //   return 1;
    //   // }
    //   // if (a.yxUidStr === user?.yxuid) {
    //   //   return -1;
    //   // }
    //   // if (b.yxUidStr === user?.yxuid) {
    //   //   return 1;
    //   // }
    //   // if (a.yxUidStr === this?.hostYxUid?.toString()) {
    //   //   return -1;
    //   // }
    //   // if (b.yxUidStr === this?.hostYxUid?.toString()) {
    //   //   return 1;
    //   // }
    //   return (a?.inTime || 0) - (b?.inTime || 0);
    // });
  }

  // @computed
  // get reactionMembers() {
  //   return [...this.membersMap.values()].filter((item) => !isEmpty(item?.reaction));
  // }

  @computed
  get sortYxuids() {
    return this.sortMembers.map((n) => n.yxUidStr);
  }

  @computed
  get whiteboardUserInfo() {
    return this.getMemberInfoByMid(this.whiteboardUid);
  }

  @computed
  get codeEditorUserInfo() {
    return this.getMemberInfoByUid(this.codeEditorEnabledUid);
  }

  @computed
  get speakerName() {
    if (this.speakerYxUid.toString() === user.yxuid) {
      //当前说话者是本地必须开启麦克风
      return rtc?.isOpenAudio ? user.name : '';
    }
    return (
      rtc?.remoteStreamsMap.get(Number(this.speakerYxUid))?.audio &&
      this.getMemberByYxuid(this.speakerYxUid.toString())?.memberName
    );
  }

  @computed
  get screenName() {
    return this.screenUserInfo?.memberName;
  }

  @computed
  get getMemberByYxuid() {
    return (yxuid: string) => {
      return this.membersMap.get(yxuid);
    };
  }

  @computed
  get appKey() {
    return this.info?.appKey;
  }

  @computed
  get memberCount() {
    return this.sortMembers.length;
  }

  @computed
  get isCalendarMode() {
    return this.info?.mode === 3 || !!this.info?.scheduleStartTime;
  }

  @computed
  get myInfo() {
    const yxuid = sessionStorage.getItem('yxuid') || '';
    return this.membersMap.get(yxuid);
  }

  @computed
  get getMemberInfoByChatId() {
    return (chatId: string) =>
      this.membersMap.get(this.chatIdToYxuidMap.get(chatId)?.toString() || '') || null;
  }

  @computed
  get getMemberInfoByUid() {
    return (uid: string) =>
      this.membersMap.get(this.uidToYxuidMap.get(uid)?.toString() || '') || null;
  }

  @computed
  get getMemberInfoByDeviceId() {
    return (deviceId: string) =>
      this.membersMap.get(this.deviceIdToYxuidMap.get(deviceId)?.toString() || '') || null;
  }

  @computed
  get getMemberInfoByMid() {
    return (mid: string) =>
      this.membersMap.get(this.midToYxuidMap.get(mid)?.toString() || '') || null;
  }
}

export default Meeting;
